import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, User, Package, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

const TYPE_META = {
  customer: { label: "Customer", Icon: User, tint: "bg-[#F0EBE0] text-[#3D2B1F] border-[#9A7B4F]/30" },
  booking:  { label: "Booking",  Icon: Package, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trial:    { label: "Trial",    Icon: Sparkles, tint: "bg-amber-50 text-amber-700 border-amber-200" },
};

const CHIPS = [
  { key: "all", label: "All" },
  { key: "customer", label: "Customers" },
  { key: "booking", label: "Bookings" },
  { key: "trial", label: "Trials" },
];

/**
 * RecipientPicker — Section 7 (Communications Recipient Selector).
 *
 * Admin-only autocomplete that searches Customers, Bookings and Trial
 * Appointments (both `trial_leads` and `appointments{type:"trial"}`) and
 * emits a single selection with all linked entity IDs pre-populated.
 *
 * Props
 *   channel: "email" | "sms"       // filters the "email required" / "phone required" hint
 *   selected?: {                    // controlled selection (optional)
 *     type, id, label, email, phone, customer_id, booking_id, trial_lead_id, appointment_id
 *   }
 *   onSelect(result): void          // fires when user picks a result
 *   onClear(): void                 // fires when user clears the selection
 *   placeholder?: string
 */
export function RecipientPicker({
  channel = "email",
  selected = null,
  onSelect,
  onClear,
  placeholder,
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({ customer: 0, booking: 0, trial: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Debounce the input by 250ms to keep search cheap.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch on debounced query / type change.
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      setCounts({ customer: 0, booking: 0, trial: 0 });
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/admin/communications/search-recipients`, {
        params: { q: debouncedQuery, type: typeFilter, limit: 8 },
        headers: auth(),
      })
      .then((res) => {
        if (cancelled) return;
        setResults(res.data?.results || []);
        setCounts(res.data?.counts || { customer: 0, booking: 0, trial: 0 });
      })
      .catch(() => {
        if (!cancelled) {
          setResults([]);
          setCounts({ customer: 0, booking: 0, trial: 0 });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, typeFilter]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const missing = useMemo(() => {
    if (!selected) return "";
    if (channel === "email" && !selected.email) return "This record has no email on file.";
    if (channel === "sms" && !selected.phone) return "This record has no phone on file.";
    return "";
  }, [selected, channel]);

  const handleSelect = (r) => {
    onSelect?.(r);
    setOpen(false);
    setQuery("");
    setDebouncedQuery("");
    setResults([]);
  };

  const handleClear = () => {
    onClear?.();
    setQuery("");
    setDebouncedQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="recipient-picker">
      <label className="block text-[10px] uppercase tracking-wider text-[#676664]/70 mb-1">
        Look up recipient
        <span className="ml-2 normal-case text-[#676664]/50 tracking-normal">
          Customer · Booking · Trial
        </span>
      </label>

      {/* Selected chip */}
      {selected ? (
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 bg-[#FAF8F0] border border-[#9A7B4F]/30"
          data-testid="recipient-picker-selected"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <TypeBadge type={selected.type} />
              <span className="text-[13px] text-[#3D2B1F] font-medium truncate">{selected.label}</span>
            </div>
            <div className="text-[10px] text-[#676664]/70 mt-0.5 truncate">
              {selected.email || selected.phone || selected.meta || ""}
            </div>
            {missing && (
              <div className="text-[10px] text-red-600 mt-0.5" data-testid="recipient-picker-missing-contact">
                {missing}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-[#676664] hover:text-[#3D2B1F] shrink-0"
            title="Clear recipient"
            data-testid="recipient-picker-clear"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          {/* Type filter chips */}
          <div className="flex flex-wrap gap-1 mb-1.5" data-testid="recipient-picker-chips">
            {CHIPS.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setTypeFilter(chip.key)}
                data-testid={`recipient-picker-chip-${chip.key}`}
                className={`px-2.5 py-1 text-[10px] uppercase tracking-wider border transition-colors ${
                  typeFilter === chip.key
                    ? "bg-[#9A7B4F] text-white border-[#9A7B4F]"
                    : "bg-white text-[#676664] border-[#282828]/12 hover:border-[#9A7B4F]/40"
                }`}
              >
                {chip.label}
                {chip.key !== "all" && counts[chip.key] > 0 && (
                  <span className="ml-1 opacity-70">({counts[chip.key]})</span>
                )}
              </button>
            ))}
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#676664]/60 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder || "Search name, email, phone, or ID…"}
              className="w-full border border-[#282828]/12 pl-7 pr-8 py-2 text-[13px]"
              data-testid="recipient-picker-input"
            />
            {loading && (
              <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9A7B4F] animate-spin" />
            )}
          </div>

          {/* Dropdown */}
          {open && (query.trim().length >= 2 || debouncedQuery.length >= 2) && (
            <div
              className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#282828]/12 shadow-lg max-h-72 overflow-y-auto"
              data-testid="recipient-picker-dropdown"
            >
              {loading && results.length === 0 && (
                <div className="px-3 py-3 text-[11px] text-[#676664]/60 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                </div>
              )}
              {!loading && results.length === 0 && debouncedQuery.length >= 2 && (
                <div className="px-3 py-3 text-[11px] text-[#676664]/60" data-testid="recipient-picker-empty">
                  No matching customers, bookings or trials.
                </div>
              )}
              {results.map((r) => (
                <button
                  key={`${r.type}-${r.id}`}
                  type="button"
                  onClick={() => handleSelect(r)}
                  className="block w-full text-left px-3 py-2 hover:bg-[#FAF8F0] border-b border-[#282828]/8 last:border-b-0"
                  data-testid={`recipient-picker-option-${r.type}-${r.id}`}
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <TypeBadge type={r.type} />
                    <span className="text-[12px] text-[#3D2B1F] font-medium truncate">{r.label}</span>
                  </div>
                  <div className="text-[10px] text-[#676664]/70 mt-0.5 truncate">{r.sublabel || r.meta}</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || TYPE_META.customer;
  const { Icon, label, tint } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${tint}`}
    >
      <Icon className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

export default RecipientPicker;
